import { cx } from '../cx'
import styles from './AspectChip.module.css'

/**
 * Spec §4 "Aspect chip":
 *   neutral     — slate-2 fill, line border
 *   invokable   — fae border + faint fill, trailing +2
 *   consequence — blood-tinted; a consequence is an invokable aspect against its owner
 *   hostile     — blood-tinted scene aspect ("On Fire!")
 */
export type AspectChipVariant = 'neutral' | 'invokable' | 'consequence' | 'hostile'

export interface AspectChipProps {
  text: string
  variant?: AspectChipVariant
  /** Free-invoke bonus shown as a trailing "+2". */
  bonus?: number
  /** Free-invoke / boost count shown as "●2" (scene aspects). */
  count?: number
  onClick?: () => void
  disabled?: boolean
  className?: string | undefined
}

export function AspectChip({
  text,
  variant = 'neutral',
  bonus,
  count,
  onClick,
  disabled = false,
  className,
}: AspectChipProps) {
  const interactive = onClick !== undefined && !disabled
  const content = (
    <>
      <span className={styles.text}>{text}</span>
      {count !== undefined && (
        <span className={styles.count}>
          {/* aria-label is prohibited on a span with no role, so the accessible
              text is a real (visually hidden) node rather than an attribute. */}
          <span aria-hidden="true">●{count}</span>
          <span className="sr-only">
            {count} free {count === 1 ? 'invoke' : 'invokes'}
          </span>
        </span>
      )}
      {bonus !== undefined && (
        <span className={styles.bonus} aria-hidden="true">
          {bonus >= 0 ? `+${bonus}` : `${bonus}`}
        </span>
      )}
    </>
  )

  if (!interactive) {
    return <span className={cx(styles.chip, styles[variant], className)}>{content}</span>
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(styles.chip, styles[variant], styles.interactive, className)}
    >
      {content}
    </button>
  )
}
